/**
 * 즐겨찾기(북마크) 안내 컴포저블
 *
 * @params none
 * @return showBookmarkHelp   버튼 클릭 시 호출하는 안내 모달/알림 함수
 * @author utis
 * @see    Ant Design Vue Modal/notification, navigator.clipboard, document.execCommand
 */
import { h } from 'vue'
import { Modal, notification } from 'ant-design-vue'

export type PlatformInfo = {
    os: 'mac' | 'windows' | 'linux' | 'ios' | 'android' | 'other'
    browser: 'chrome' | 'safari' | 'edge' | 'firefox' | 'other'
}

/**
 * UA 기반 플랫폼/브라우저 감지 (클라이언트 전용)
 *
 * @params none
 * @return PlatformInfo
 * @author utis
 * @see    navigator.userAgent
 */
function detectPlatform(): PlatformInfo {
    if (typeof navigator === 'undefined') {
        return { os: 'other', browser: 'other' }
    }
    const ua = navigator.userAgent.toLowerCase()
    const isMac = /macintosh|mac os x/.test(ua)
    const isIOS = /iphone|ipad|ipod/.test(ua)
    const isAndroid = /android/.test(ua)
    const isWin = /windows/.test(ua)
    const isLinux = /linux/.test(ua) && !isAndroid

    const browser: PlatformInfo['browser'] =
        /edg\//.test(ua) ? 'edge'
            : /chrome\//.test(ua) && !/edg\//.test(ua) && !/opr\//.test(ua) ? 'chrome'
                : /safari\//.test(ua) && !/chrome\//.test(ua) ? 'safari'
                    : /firefox\//.test(ua) ? 'firefox'
                        : 'other'

    const os: PlatformInfo['os'] =
        isIOS ? 'ios'
            : isAndroid ? 'android'
                : isMac ? 'mac'
                    : isWin ? 'windows'
                        : isLinux ? 'linux'
                            : 'other'

    return { os, browser }
}

/**
 * URL 복사 (폴백 방식 포함)
 *
 * @params url  복사할 주소
 * @return Promise<void>
 * @author utis
 * @see    navigator.clipboard.writeText, document.execCommand('copy')
 */
async function copyUrl(url: string): Promise<void> {
    if (typeof window === 'undefined') {
        notification.warning({
            message: '복사 불가',
            description: '서버 환경에서는 복사할 수 없습니다.',
            placement: 'top',
        })
        return
    }

    // 방법 1: Clipboard API 시도
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        try {
            await navigator.clipboard.writeText(url)
            notification.success({
                message: '링크가 복사되었습니다',
                description: url,
                placement: 'top',
            })
            return
        } catch (error) {
            console.warn('Clipboard API 실패, 폴백 방식 시도:', error)
            // 폴백 방식으로 계속 진행
        }
    }

    // 방법 2: execCommand 폴백 (구형 브라우저 및 HTTPS 이슈 대응)
    try {
        const textarea = document.createElement('textarea')
        textarea.value = url
        textarea.style.position = 'fixed'
        textarea.style.left = '-9999px'
        textarea.style.top = '-9999px'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)

        // iOS Safari 대응
        textarea.contentEditable = 'true'
        textarea.readOnly = false

        // 텍스트 선택
        if (navigator.userAgent.match(/ipad|iphone/i)) {
            // iOS 전용 선택 방식
            const range = document.createRange()
            range.selectNodeContents(textarea)
            const selection = window.getSelection()
            if (selection) {
                selection.removeAllRanges()
                selection.addRange(range)
            }
            textarea.setSelectionRange(0, textarea.value.length)
        } else {
            textarea.select()
            textarea.setSelectionRange(0, textarea.value.length)
        }

        const successful = document.execCommand('copy')
        document.body.removeChild(textarea)

        if (successful) {
            notification.success({
                message: '링크가 복사되었습니다',
                description: url,
                placement: 'top',
            })
        } else {
            throw new Error('execCommand 실패')
        }
    } catch (error) {
        console.error('복사 실패:', error)
        notification.error({
            message: '복사 실패',
            description: '링크를 수동으로 복사해주세요: ' + url,
            placement: 'top',
            duration: 5,
        })
    }
}

export function useBookmarkHint() {
    /**
     * 클릭 시 북마크 안내 모달 표시
     *
     * @params url 현재 페이지 URL (기본값: location.href)
     * @return void
     * @author utis
     * @see    detectPlatform, copyUrl, Ant Design Vue Modal.confirm
     */
    function showBookmarkHelp(url = (typeof location !== 'undefined' ? location.href : '')): void {
        if (typeof window === 'undefined') return

        const { os, browser } = detectPlatform()

        let guide = ''
        if (os === 'mac') guide = '⌘ + D 를 눌러 즐겨찾기에 추가하세요.'
        else if (os === 'windows' || os === 'linux') guide = 'Ctrl + D 를 눌러 즐겨찾기에 추가하세요.'
        else if (os === 'ios') {
            guide = browser === 'safari'
                ? "Safari 하단의 공유(□↑) → '책갈피 추가'를 탭하세요."
                : "브라우저 메뉴에서 '책갈피 추가' 또는 공유(□↑) → '책갈피 추가'를 탭하세요."
        } else if (os === 'android') {
            guide = browser === 'chrome'
                ? "우측 상단 ••• 메뉴 → ★ 아이콘(북마크)을 탭하세요."
                : "브라우저 메뉴에서 '북마크' 또는 '즐겨찾기 추가'를 탭하세요."
        } else {
            guide = '브라우저의 북마크/즐겨찾기 메뉴를 사용해주세요.'
        }

        const contentVNode = h('div', null, [
            h('p', { style: 'margin-bottom:8px;' }, guide),
            h('p', { style: 'margin-bottom:8px;color:#888;' }, `현재 페이지: ${url}`),
            h('p', { style: 'margin-bottom:0;' }, '단축키로 추가가 어려우면 아래 버튼으로 링크를 복사하세요.'),
        ])

        Modal.confirm({
            title: '즐겨찾기 안내',
            icon: null,
            content: contentVNode,
            okText: '링크 복사',
            cancelText: '닫기',
            centered: true,
            maskClosable: true,
            onOk: () => copyUrl(url),
            onCancel: () => {},
        })
    }

    return { showBookmarkHelp }
}